import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, Callout, Checkbox, OptionCard, Segmented } from '@/components/ui';
import { EXPORT_COLUMNS, type ExportScope, type ScopedAccountExportVM } from './useScopedAccountExport';
import type { ScopedAccountsVM } from './useScopedAccounts';
import styles from './ExportAccountsPopover.module.css';

/**
 * EAA-1839 (frame 35d): the "Xuất CSV" popover, anchored under its own button
 * rather than opened as a modal — the frame draws it that way because every choice
 * in it is a narrowing of what is already on screen behind it, and a modal would
 * hide the row count the "đang hiển thị" option refers to.
 *
 * See `useScopedAccountExport` for what the export really does and for each of the
 * four honest limits surfaced here (the 2,000-row cap, "toàn bộ nhánh" clearing
 * filters but never the scope, XLSX being unavailable, no audit row).
 */
export function ExportAccountsPopover({
    vm,
    exporter,
}: {
    vm: ScopedAccountsVM;
    exporter: ScopedAccountExportVM;
}) {
    const { t } = useTranslation();

    const scopeOptions: { value: ExportScope; count: number | null }[] = [
        { value: 'page', count: vm.rows.length },
        { value: 'filtered', count: vm.totalCount },
        { value: 'branch', count: null },
    ];

    const content = (
        <div className={styles.export}>
            <fieldset className={styles.group}>
                <legend className={styles.legend}>
                    {t('console.hierarchy.scopedAccounts.export.scopeLegend')}
                </legend>
                {scopeOptions.map((option) => (
                    <OptionCard
                        key={option.value}
                        className="sa-export__scope"
                        name="sa-export-scope"
                        value={option.value}
                        selected={exporter.scope === option.value}
                        onSelect={() => exporter.setScope(option.value)}
                        title={t(`console.hierarchy.scopedAccounts.export.scope_${option.value}`, {
                            count: option.count ?? 0,
                        })}
                        description={t(
                            `console.hierarchy.scopedAccounts.export.scopeHint_${option.value}`
                        )}
                    />
                ))}
            </fieldset>

            <fieldset className={styles.group}>
                <legend className={styles.legend}>
                    {t('console.hierarchy.scopedAccounts.export.columnsLegend')}
                    <Button type="link" size="small" onClick={exporter.selectAllColumns}>
                        {t('console.hierarchy.scopedAccounts.export.selectAllColumns')}
                    </Button>
                </legend>
                <div className={styles.columns}>
                    {EXPORT_COLUMNS.map((column) => (
                        <Checkbox
                            key={column}
                            checked={exporter.columns.includes(column)}
                            onChange={() => exporter.toggleColumn(column)}
                        >
                            {exporter.headerFor(column)}
                        </Checkbox>
                    ))}
                </div>
            </fieldset>

            <fieldset className={styles.group}>
                <legend className={styles.legend}>
                    {t('console.hierarchy.scopedAccounts.export.formatLegend')}
                </legend>
                <Segmented
                    size="small"
                    value="csv"
                    options={[
                        { value: 'csv', label: 'CSV' },
                        {
                            value: 'xlsx',
                            label: 'XLSX',
                            disabled: true,
                            title: t('console.hierarchy.scopedAccounts.export.xlsxUnavailable'),
                        },
                    ]}
                    aria-label={t('console.hierarchy.scopedAccounts.export.formatLegend')}
                />
            </fieldset>

            {exporter.willBeCapped && (
                <Callout tone="warning" longLabel>
                    {t('console.hierarchy.scopedAccounts.export.cappedWarning', {
                        max: exporter.maxRows.toLocaleString('vi-VN'),
                        total: exporter.scopeCount.toLocaleString('vi-VN'),
                    })}
                </Callout>
            )}
            {exporter.capped && (
                <Callout tone="warning" longLabel>
                    {t('console.hierarchy.scopedAccounts.export.cappedDone', {
                        max: exporter.maxRows.toLocaleString('vi-VN'),
                    })}
                </Callout>
            )}

            <div className={styles.footer}>
                <Button onClick={() => exporter.setOpen(false)} disabled={exporter.busy}>
                    {t('common.cancel')}
                </Button>
                <Button
                    type="primary"
                    loading={exporter.busy}
                    disabled={exporter.columns.length === 0}
                    onClick={() => void exporter.run()}
                >
                    {t('console.hierarchy.scopedAccounts.export.submitButton')}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            open={exporter.open}
            onOpenChange={exporter.setOpen}
            trigger="click"
            placement="bottomRight"
            content={content}
            overlayClassName="sa-export-popover"
        >
            <Button>{t('console.hierarchy.scopedAccounts.header.exportCsv')}</Button>
        </Popover>
    );
}

export default ExportAccountsPopover;
